import { useQuery } from 'react-query';
import React from 'react';
import useDateRangePicker from '../../../components/organisms/forms/date-range-picker/reusable-func';
import api from '../../../components/api';
import getScheduleByDateJSON from './dummyDataRes/getScheduleByDate.json';
import getTotalSalesJSON from './dummyDataRes/getTotalSales.json';
import getOverviewSalesJSON from './dummyDataRes/getOverviewSales.json';
import getAllRoutesLastWeeklySales from './dummyDataRes/getAllRoutesLastWeeklySales.json';
import { DAY_OPTIONS } from '../../../components/atoms/const';
import { VendingMachineData } from '../vending-machine/hooks';

export interface ServiceTicket {
  id: number;
  deviceId: string;
  name: string;
  email: string;
}

export interface Schedule {
  id: number;
  name: string;
  driver: string;
  serviceDay: string;
}

export interface RouteSalesData {
  total: {
    actualAmount: number
    amount: number
    clientId: any
    clientName: any
    dateKey: string;
    deviceId: any;
    isDrink: boolean;
    name: string;
    numberOfClients: number;
    quantity: number;
    vmId: any;
    vmLocation: any;
  }
}

function useOverview() {
  /*
 *
 *
 *
 *
 *
 *
 *
 */

  /*
  * Get Full Name from Session Storage that are set when login
  */
  const fullName = React.useMemo(() => sessionStorage.getItem('fullName'), [sessionStorage]);

  /*
  *
  *
  *
  *
  *
  *
  *
  */

  /*
  * Handle Current View on Chart
  */
  const [currentView, setCurrentView] = React.useState<number>(0);

  /*
  *
  *
  *
  *
  *
  *
  *
  */

  /*
  * Get Initial Data
  * 1. Service Ticket List TOP 10
  * 2. Route Schedule By Date TOP 10 in range 2 weeks
  * 3. Total Sales : Today, Yesterday, This Week, Last Week, This Month, Last Month
  * 4. Get Overview Sales
  * 5. Get All Routes Last Weekly Sales
  * 6. Checker new user : Get All Active Clients, Get Vending Machine, get Coffee Machine
  */
  // 1. Get Service Ticket List
  const { data: serviceTicketsRes, isLoading: serviceTicketsLoad } = useQuery(
    ['getAllServiceRequests', 'customerSupport'],
    () => api.customerSupport.getAllServiceRequests(),
  );
  // 1.1. Data Management of Service Ticket List Response
  const serviceTickets: ServiceTicket[] = React.useMemo(() => {
    const resData = serviceTicketsRes?.data?.data;
    if (resData) {
      return resData.map((data) => ({
        id: data.id,
        deviceId: data.deviceId ?? '-',
        name: data && data.user ? `${data.user.firstname} ${data.user.lastname}` : '-',
        email: data.email ?? '-',
      }));
    }
    return [];
  }, [serviceTicketsRes]);

  const twoWeekLater = new Date(new Date().setDate(new Date().getDate() + 14));
  const getDateFormat = (date: Date) => `${date.getMonth() + 1}/${date.getDate()}/${date.getFullYear()}`;

  // 2. Route Schedule By Date
  const { data: scheduleRes, isLoading: scheduleLoad } = useQuery(
    ['getByDateRange', 'schedule', getDateFormat(new Date()), getDateFormat(twoWeekLater), 0, 10],
    () => api.schedules.getByDateRange(getDateFormat(new Date()), getDateFormat(twoWeekLater), 0, 10),
  );
  const scheduleList: Schedule[] = React.useMemo(() => {
    const resData: typeof getScheduleByDateJSON [] | undefined = scheduleRes?.data?.data;
    if (resData) {
      return resData.map((data) => ({
        id: data.id,
        name: data.name ?? '-',
        driver: data && data.user ? `${data.user.firstname} ${data.user.lastname}` : '-',
        serviceDay: data.date ? DAY_OPTIONS.find((x) => x.value === new Date(data.date).getDay())?.label ?? '-' : '-',
      }));
    }
    return [];
  }, [scheduleRes]);

  // 3. Get Total Sales
  const { data: totalSalesRes, isLoading: totalSalesLoad } = useQuery(
    ['getTotalSales', 'overview'],
    () => api.overview.getTotalSales(),
  );
  const totalSales: typeof getTotalSalesJSON | undefined = React.useMemo(() => {
    const resData: typeof getTotalSalesJSON | undefined = totalSalesRes?.data?.data;
    if (resData) return resData;
    return undefined;
  }, [totalSalesRes]);

  // 4. Get Overview Sales
  // Should be changed everytime currentView changing
  // Current View current Number :
  // 0. Today
  // 1. This Week
  // 2. This Month
  // 3. Yesterday
  // 4. Last Week
  // 5. Last Month
  const { data: overviewSalesRes, isLoading: overviewSalesLoad } = useQuery(
    ['getOverviewSales', 'overview'],
    () => api.overview.getOverviewSales(),
  );
  const overviewSales: {soda: number[], snack: number[], label: string[]} | undefined = React.useMemo(() => {
    const resData: typeof getOverviewSalesJSON | undefined = overviewSalesRes?.data?.data;
    if (resData) {
      // Checking data is empty or not on daily,weekly, or monthly, zero count means empty
      const arrCounter: any = Object.values(resData);
      let count = 0;
      for (let i = 0; i < arrCounter.length; i += 1) {
        count += Object.keys(arrCounter[i]).length;
      }
      if (count === 0) {
        return undefined;
      }

      // Logic when data is not empty
      const dateConverter = (datekey: string): Date | undefined => {
        if (datekey) {
          const year = datekey.slice(0, 4) !== '' ? datekey.slice(0, 4) : new Date().getFullYear().toString();
          const month = datekey.slice(4, 6) !== '' ? datekey.slice(4, 6) : (new Date().getMonth() + 1).toString();
          const date = datekey.slice(6, 8) !== '' ? datekey.slice(6, 8) : (new Date().getDate()).toString();
          return new Date(parseInt(year, 10), parseInt(month, 10) - 1, parseInt(date, 10));
        }
        return undefined;
      };
      if (currentView === 0 || currentView === 3) {
        return {
          soda: resData?.daily?.Soda?.map((x) => x.amount ?? 0).reverse(),
          snack: resData?.daily?.Snack?.map((x) => x.amount ?? 0).reverse(),
          label: resData?.daily?.Snack?.map((x) => `${dateConverter(x.dateKey)?.toString().split(' ')[2]} ${dateConverter(x.dateKey)?.toString().split(' ')[1]}`).reverse(),
        };
      }
      if (currentView === 1 || currentView === 4) {
        return {
          soda: resData?.weekly?.Soda?.map((x) => x.amount ?? 0).reverse(),
          snack: resData?.weekly?.Snack?.map((x) => x.amount ?? 0).reverse(),
          label: resData?.weekly?.Snack?.map((x) => `Week ${x.dateKey?.slice(4, 6)}`).reverse(),
        };
      }
      if (currentView === 2 || currentView === 5) {
        return {
          soda: resData?.monthly?.Soda?.map((x) => x.amount ?? 0).reverse(),
          snack: resData?.monthly?.Snack?.map((x) => x.amount ?? 0).reverse(),
          label: resData?.monthly?.Snack?.map((x) => `${dateConverter(x.dateKey)?.toString().split(' ')[1]} ${dateConverter(x.dateKey)?.toString().split(' ')[3]}`).reverse(),
        };
      }
      return {
        soda: [],
        snack: [],
        label: [],
      };
    }
    return undefined;
  }, [overviewSalesRes, currentView]);

  // 5. Get All Routes Last Weekly Sales
  const { data: routeSalesRes, isLoading: routeSalesLoad } = useQuery(
    ['getAllRoutesLastWeeklySales', 'overview'],
    () => api.overview.getAllRoutesLastWeeklySales(),
  );
  const routeSales: RouteSalesData[] = React.useMemo(() => {
    const resData : typeof getAllRoutesLastWeeklySales | undefined = routeSalesRes?.data?.data;
    if (resData) {
      return Object.entries(resData)?.map((route) => route[1]);
    }
    return [];
  }, [routeSalesRes]);

  // Handling Route Current View to get Amount Data
  const [currentViewRoute, setCurrentViewRoute] = React.useState<number>(0);
  const amountRoute: number = React.useMemo(() => {
    if (routeSales.length) return routeSales[currentViewRoute]?.total?.amount ?? 0;
    return 0;
  }, [routeSales, currentViewRoute]);

  // 6. Checker Client, Vending Machine, Coffee
  // 6.1. Client
  const { isLoading: loadingClientList, data: clientListRes } = useQuery(
    ['getAllActive', 'clients'],
    () => api.clients.getAllActive(),
  );
  const clientList: boolean = React.useMemo(() => {
    const resData: object[] | undefined = clientListRes?.data?.data;
    return !!(resData && resData.length);
  }, [clientListRes]);

  // 6.2. Vending Machine
  const { isLoading: vmListLoading, data: vmListRes } = useQuery(
    ['findAll', 'vendingMachines', ''],
    () => api.vendingMachine.findAll(''),
  );
  const vmList: boolean = React.useMemo(() => {
    const resData: VendingMachineData[] | undefined = vmListRes?.data?.data;
    return !!(resData && resData.length);
  }, [vmListRes]);
  /*
  *
  *
  *
  *
  *
  *
  *
  */

  /*
  * Date Picker Handler
  */
  const { handleChangeEndDate, handleChangeStartDate, selectedDate } = useDateRangePicker();
  return {
    // user full name
    fullName,

    // date handler
    handleChangeEndDate,
    handleChangeStartDate,
    selectedDate,

    // service ticket table data
    serviceTickets,
    serviceTicketsLoad,

    // route schedule table data
    scheduleList,
    scheduleLoad,

    // total sales for chart
    totalSales,
    totalSalesLoad,

    // overview sales chart
    overviewSales,
    overviewSalesLoad,

    // route sales chart
    routeSales,
    routeSalesLoad,
    amountRoute,

    // checker client, vending machine
    clientList,
    loadingClientList,
    vmList,
    vmListLoading,

    // chart current view handler
    currentView,
    setCurrentView,
    currentViewRoute,
    setCurrentViewRoute,
  };
}

export default useOverview;
